import type {
  InfoLine,
  LegendOptions,
} from '@/features/legends/options/legend-options'
import type { Question } from '@/features/legends/questions/questions'
import type { Routing } from '@/features/legends/routing/routing'
import type { Template } from '@/features/legends/templates/templates'

/**
 * The content of a legend version as the builder edits it.
 */
export interface LegendDocument {
  options: LegendOptions
  questions: Question[]
  infoLines?: InfoLine[]
  routing?: Routing
  templates?: Template[]
}

/**
 * The document of a legend version without content.
 */
export const emptyLegendDocument: LegendDocument = {
  options: {},
  questions: [],
}

/**
 * The IDs of the service items the legend's elements add.
 *
 * @param document
 */
export function getDocumentInventoryItemIds(document: LegendDocument) {
  return flattenQuestions(document.questions).flatMap((question) =>
    question.type === 'InventoryItem' && question.inventoryItemId
      ? [question.inventoryItemId]
      : [],
  )
}

/**
 * The document with the elements that add a service item pointed at another
 * item.
 *
 * @param document
 * @param previousItemId
 * @param newItemId
 */
export function replaceDocumentInventoryItem(
  document: LegendDocument,
  previousItemId: string,
  newItemId: string,
): LegendDocument {
  const replace = (question: Question): Question => {
    switch (question.type) {
      case 'InventoryItem':
        return question.inventoryItemId === previousItemId
          ? { ...question, inventoryItemId: newItemId }
          : question
      case 'Path':
        return { ...question, questions: question.questions?.map(replace) }
      default:
        return question
    }
  }
  return { ...document, questions: document.questions.map(replace) }
}

function flattenQuestions(questions: Question[]): Question[] {
  return questions.flatMap((question) =>
    question.type === 'Path'
      ? flattenQuestions(question.questions ?? [])
      : [question],
  )
}

/**
 * An engine value type.
 */
export type ValueType =
  | { type: 'Number' }
  | { type: 'String' }
  | { type: 'Boolean' }
  | { type: 'Date' }
  | { type: 'List'; itemType: ValueType }
  | UnknownElement

/**
 * A part of the legend of a type this builder doesn't know, which a newer
 * builder or engine saved. The builder can't edit or save the part it's in.
 */
export interface UnknownElement {
  type: 'Unknown'
  /**
   * The type as the Legend API sent it.
   */
  resourceType: string
}

/**
 * An element of a type the builder doesn't know is in the legend.
 */
export class UnknownElementError extends Error {
  /**
   * @param kind What the element is, like "question".
   * @param element
   */
  constructor(kind: string, element: UnknownElement) {
    super(`Unknown ${kind} type "${element.resourceType}"`)
    this.name = 'UnknownElementError'
  }
}

/**
 * Maps a resource of a type the client doesn't know. Called after a switch
 * over every type the resource can have, which narrows it to `never`, so that
 * a new type breaks the build; the server can still send one.
 *
 * @param resource
 */
export function mapUnknownResourceToElement(resource: never): UnknownElement {
  // SAFETY: every resource union this is called for is discriminated by
  // `type`.
  const { type } = resource as { type: unknown }
  return { type: 'Unknown', resourceType: String(type) }
}
